import React from 'react';
import { Link } from 'react-router-dom';
import { Display, Meta, Rule } from '../components/Atoms';

/**
 * PrivacyPolicy - Polityka prywatności RODO/GDPR
 *
 * Publiczny ekran (bez auth). Stylistyka editorial / Fraunces + DM Sans.
 */

function PolicySection({ number, title, children }) {
  return (
    <section style={{ marginBottom: 36 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 14,
        marginBottom: 14,
        paddingBottom: 10,
        borderBottom: '1px solid var(--t-line-2)',
      }}>
        <span style={{
          fontFamily: 'var(--t-mono)', fontSize: 11,
          letterSpacing: '.18em', color: 'var(--t-amber)',
        }}>§ {number}</span>
        <Display size={22} style={{ display: 'inline', letterSpacing: '-.02em' }}>
          {title}
        </Display>
      </div>
      <div style={{
        fontSize: 14, color: 'var(--t-text-dim)',
        lineHeight: 1.7,
      }}>{children}</div>
    </section>
  );
}

function Bullet({ label, children }) {
  return (
    <li style={{
      paddingLeft: 16, position: 'relative',
      marginBottom: 8,
      listStyle: 'none',
    }}>
      <span style={{
        position: 'absolute', left: 0, top: 0,
        color: 'var(--t-sage)',
      }}>·</span>
      <strong style={{ color: 'var(--t-text)', fontWeight: 500 }}>{label}:</strong> {children}
    </li>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="redesign-root" style={{
      minHeight: 'var(--t-vh)',
      background: 'var(--t-bg)',
      color: 'var(--t-text)',
      fontFamily: 'var(--t-sans)',
    }}>
      <div style={{
        maxWidth: 720, margin: '0 auto',
        padding: '60px 32px 80px',
      }}>
        <Link to="/" style={{
          fontFamily: 'var(--t-mono)', fontSize: 11,
          letterSpacing: '.18em', textTransform: 'uppercase',
          color: 'var(--t-sage)', textDecoration: 'none',
          marginBottom: 30, display: 'inline-block',
        }}>← Strona główna</Link>

        <Meta color="sage" style={{ marginBottom: 12 }}>Dokumenty prawne</Meta>
        <Display size={'clamp(40px, 7vw, 64px)'} style={{
          display: 'block', letterSpacing: '-.03em', lineHeight: 0.95,
          marginBottom: 18,
        }}>Polityka prywatności</Display>

        <div style={{
          fontSize: 13, color: 'var(--t-text-mute)',
          fontFamily: 'var(--t-mono)', letterSpacing: '.1em',
          marginBottom: 36,
        }}>
          Aktualizacja: {new Date().toLocaleDateString('pl-PL', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>

        <div style={{
          fontSize: 16, color: 'var(--t-text-dim)',
          lineHeight: 1.65, marginBottom: 50,
          paddingLeft: 18,
          borderLeft: '2px solid var(--t-line-2)',
        }}>
          Niniejsza polityka opisuje, w jaki sposób GardenApp.pl zbiera, wykorzystuje i chroni
          Twoje dane osobowe. Aplikacja jest darmowa, nie sprzedaje danych i nie wykorzystuje
          cookies śledzących.
        </div>

        <PolicySection number="1" title="Jakie dane zbieramy">
          <ul style={{ padding: 0, margin: 0 }}>
            <Bullet label="Konto">nazwa użytkownika, adres email, hasło (zahashowane bcrypt).</Bullet>
            <Bullet label="Lokalizacja">współrzędne geograficzne i miasto (do prognozy pogody).</Bullet>
            <Bullet label="Ogród">działki, grządki, rośliny, zadania, zbiory, opryski.</Bullet>
            <Bullet label="Znajomi (opcjonalnie)">lista znajomych, zaproszenia, aktywność — tylko gdy obie strony zaakceptują znajomość. Dane społeczne możesz pobrać w eksporcie.</Bullet>
            <Bullet label="Blokady/Zgłoszenia">lista zablokowanych użytkowników i wysłane zgłoszenia — przechowywane do celów moderacji.</Bullet>
            <Bullet label="Techniczne">data ostatniego logowania, preferencje (tryb ciemny, język).</Bullet>
            <Bullet label="Zdjęcia">opcjonalnie przesyłane zdjęcia roślin i działek.</Bullet>
            <Bullet label="Społeczne (opcjonalnie)">jeśli włączysz profil publiczny: relacje znajomości, lista znajomych, blokady, zgłoszenia. Tylko za Twoją zgodą (opt-in).</Bullet>
          </ul>
        </PolicySection>

        <PolicySection number="2" title="Cel przetwarzania danych">
          <ul style={{ padding: 0, margin: 0 }}>
            <Bullet label="Świadczenie usług">umożliwienie korzystania z aplikacji, zarządzanie kontem.</Bullet>
            <Bullet label="Funkcje pogodowe">wyświetlanie prognozy dla Twojej lokalizacji.</Bullet>
            <Bullet label="Personalizacja">dostosowanie aplikacji do preferencji.</Bullet>
            <Bullet label="Społeczność">umożliwienie interakcji między użytkownikami (znajomi, feed, wiadomości) — tylko jeśli wyrazisz zgodę włączając profil publiczny.</Bullet>
            <Bullet label="Bezpieczeństwo">ochrona przed nieautoryzowanym dostępem.</Bullet>
            <Bullet label="Ulepszenia">analiza użytkowania w celu poprawy aplikacji.</Bullet>
          </ul>
        </PolicySection>

        <PolicySection number="3" title="Pliki cookies">
          <ul style={{ padding: 0, margin: 0, marginBottom: 14 }}>
            <Bullet label="Niezbędne">token uwierzytelniający (JWT) w httpOnly cookie — wymagany do logowania.</Bullet>
            <Bullet label="Funkcjonalne">preferencje użytkownika (tryb ciemny, zgoda na cookies).</Bullet>
            <Bullet label="Brak śledzących">nie używamy cookies analitycznych ani reklamowych.</Bullet>
          </ul>
          <p style={{ marginTop: 14 }}>
            Możesz zarządzać cookies przez ustawienia przeglądarki. Usunięcie cookies niezbędnych
            uniemożliwi korzystanie z aplikacji.
          </p>
        </PolicySection>

        <PolicySection number="4" title="Udostępnianie danych osobom trzecim">
          <p style={{ marginBottom: 14 }}>Korzystamy z następujących zewnętrznych usług:</p>
          <ul style={{ padding: 0, margin: 0, marginBottom: 14 }}>
            <Bullet label="OpenWeatherMap">współrzędne geograficzne, wyłącznie do pobrania prognozy pogody.</Bullet>
            <Bullet label="Gmail SMTP">adres email do wysyłki powiadomień (rejestracja, reset hasła, alerty).</Bullet>
          </ul>
          <p style={{
            marginTop: 14, padding: '12px 16px', borderRadius: 10,
            background: 'rgba(168, 196, 158, 0.06)',
            borderLeft: '3px solid var(--t-sage)',
            color: 'var(--t-text)',
          }}>
            <strong>Nie sprzedajemy ani nie wynajmujemy Twoich danych.</strong> Nie przekazujemy
            ich do celów marketingowych ani reklamowych.
          </p>
        </PolicySection>

        <PolicySection number="5" title="Bezpieczeństwo danych">
          <ul style={{ padding: 0, margin: 0 }}>
            <Bullet label="Szyfrowanie">połączenia HTTPS (TLS 1.3), hasła hashowane bcrypt z salt.</Bullet>
            <Bullet label="Tokeny w httpOnly cookies">JWT niedostępny dla JavaScriptu (ochrona XSS).</Bullet>
            <Bullet label="Rate limiting">ochrona przed atakami brute-force na endpointy auth.</Bullet>
            <Bullet label="Walidacja po stronie serwera">każda mutacja wraz z sanityzacją.</Bullet>
            <Bullet label="Aktualizacje">systemu, kontenerów Docker i zależności.</Bullet>
          </ul>
        </PolicySection>

        <PolicySection number="6" title="Okres przechowywania danych">
          <ul style={{ padding: 0, margin: 0 }}>
            <Bullet label="Konto aktywne">do momentu usunięcia konta lub na żądanie.</Bullet>
            <Bullet label="Soft delete">konto skasowane jest przechowywane 30 dni (możliwość przywrócenia z linka emailowego), potem usuwane bezpowrotnie.</Bullet>
            <Bullet label="Dane ogrodnicze">do momentu usunięcia przez użytkownika lub konta.</Bullet>
            <Bullet label="Logi systemowe">maksymalnie 30 dni.</Bullet>
          </ul>
        </PolicySection>

        <PolicySection number="7" title="Twoje prawa (RODO)">
          <p style={{ marginBottom: 14 }}>Zgodnie z RODO masz prawo do:</p>
          <ul style={{ padding: 0, margin: 0, marginBottom: 14 }}>
            <Bullet label="Dostępu">uzyskania kopii swoich danych (Profil → Eksport).</Bullet>
            <Bullet label="Sprostowania">poprawienia nieprawidłowych danych w ustawieniach.</Bullet>
            <Bullet label="Usunięcia">usunięcia konta i wszystkich danych (Profil → Konto → Strefa niebezpieczna).</Bullet>
            <Bullet label="Przenoszenia">eksportu danych w formacie JSON (Eksport pełny backup).</Bullet>
            <Bullet label="Sprzeciwu">wycofania zgody na przetwarzanie.</Bullet>
          </ul>
          <p>
            Aby skorzystać z powyższych praw, zaloguj się i przejdź do ustawień profilu lub
            skontaktuj się z administratorem.
          </p>
        </PolicySection>

        <PolicySection number="8" title="Zmiany w polityce prywatności">
          <p>
            Zastrzegamy sobie prawo do aktualizacji niniejszej polityki. O istotnych zmianach
            poinformujemy użytkowników w aplikacji lub mailem. Data ostatniej aktualizacji
            znajduje się na górze dokumentu.
          </p>
        </PolicySection>

        <PolicySection number="9" title="Kontakt">
          <p style={{ marginBottom: 16 }}>
            W razie pytań dotyczących przetwarzania danych osobowych skontaktuj się z administratorem:
          </p>
          <div style={{
            padding: '14px 18px', borderRadius: 10,
            background: 'var(--t-bg-elev)',
            border: '1px solid var(--t-line)',
            fontFamily: 'var(--t-mono)', fontSize: 13,
            color: 'var(--t-text)',
            letterSpacing: '.04em', lineHeight: 1.7,
          }}>
            GardenApp.pl<br />
            email: hexan@tlen.pl<br />
            web: gardenapp.pl
          </div>
        </PolicySection>

        <Rule label="koniec dokumentu" />

        <div style={{ marginTop: 40, textAlign: 'center' }}>
          <Link to="/" style={{
            display: 'inline-block',
            padding: '14px 28px', borderRadius: 12,
            background: 'var(--t-sage)', color: 'var(--t-bg)',
            fontFamily: 'var(--t-sans)', fontSize: 14,
            fontWeight: 500, letterSpacing: '-.01em',
            textDecoration: 'none',
          }}>Powrót do aplikacji</Link>
        </div>
      </div>
    </div>
  );
}
